import React, { useEffect } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import './App.css';

import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Tasks from './pages/Tasks';
import IncompleteTasks from './pages/IncompleteTasks';
import Calendar from './pages/Calendar';
import Clients from './pages/Clients';
import Finances from './pages/Finances';
import Analytics from './pages/Analytics';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Notes from './pages/Notes';
import Warehouse from './pages/Warehouse';
import Documents from './pages/Documents';
import Objects from './pages/Objects';
import Layout from './components/Layout';
import ToastViewport from './components/ToastViewport';
import { AuthProvider, useAuth } from './services/AuthContext';
import { SettingsProvider } from './services/SettingsContext';
import syncService from './services/syncService';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // Показываем загрузку, пока проверяется аутентификация
  if (loading) {
    return <div className="spinner"></div>;
  }

  // Если не авторизован, перенаправляем на страницу входа
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return children;
};

const NativeBackHandler = () => {
  const location = useLocation();

  useEffect(() => {
    let listenerHandle = null;
    let cancelled = false;

    const register = async () => {
      try {
        const handle = await CapacitorApp.addListener('backButton', async () => {
          const hasOpenLayer = document.querySelector(
            '.waiting-dialog-backdrop, .task-form-modal, .sidebar.open'
          );
          if (hasOpenLayer) {
            window.dispatchEvent(new CustomEvent('crm:request-back'));
            return;
          }

          if (!['/', '/tasks', '/login'].includes(location.pathname)) {
            window.dispatchEvent(new CustomEvent('crm:request-back'));
            return;
          }

          try {
            await CapacitorApp.minimizeApp();
          } catch {
            // В браузере и на платформах без minimizeApp ничего не делаем.
          }
        });

        if (cancelled) {
          await handle.remove();
        } else {
          listenerHandle = handle;
        }
      } catch (error) {
        console.warn('Native back button is unavailable:', error);
      }
    };

    void register();
    return () => {
      cancelled = true;
      if (listenerHandle) void listenerHandle.remove();
    };
  }, [location.pathname]);

  return null;
};

function AppContent() {
  useEffect(() => {
    // Инициализация сервисов синхронизации при загрузке приложения
    const initSyncServices = async () => {
      try {
        await syncService.init();

      } catch (error) {
        console.error('Error initializing sync services:', error);
      }
    };

    initSyncServices();
  }, []);

  return (
    <Router>
        <div className="App">
          <NativeBackHandler />
          <ToastViewport />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<Navigate to="/tasks" replace />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="tasks/incomplete" element={<IncompleteTasks />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="clients" element={<Clients />} />
              <Route path="finances" element={<Finances />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="profile" element={<Profile />} />
              <Route path="settings" element={<Settings />} />
              <Route path="notes" element={<Notes />} />
              <Route path="warehouse" element={<Warehouse />} />
              <Route path="documents" element={<Documents />} />
              <Route path="objects" element={<Objects />} />
            </Route>
          </Routes>
        </div>
      </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <AppContent />
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
